import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Lock, Plus, Trash2, Vote } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Kpi, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const iso = (local) => (local ? `${local}:00+00:00` : "");
const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");
const words = (s) => String(s || "").replace(/_/g, " ");

const F = ({ label, hint, children, className }) => (
  <div className={`space-y-1.5 ${className || ""}`}>
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

export const BallotBuilder = ({ electionRef, onChanged }) => {
  const [data, setData] = useState(null);
  const [turnout, setTurnout] = useState(null);
  const [build, setBuild] = useState(null);
  const [window_, setWindow] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [b, t] = await Promise.all([
      api.get(`/elections/${electionRef}/ballot`),
      api.get(`/elections/${electionRef}/turnout`),
    ]);
    setData(b.data);
    setTurnout(t.data);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setBuild(null); setWindow(null);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Panel title="Ballot" testId="panel-ballot-loading">
    <p className="text-[12.5px] text-muted-foreground">Opening the ballot…</p></Panel>;

  const ballot = data.ballot;
  const frozen = ballot && ballot.status !== "draft";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="ballot-kpi-status" label="Ballot" icon={Vote}
             value={ballot ? words(ballot.status) : "not drawn"}
             sub={ballot ? ballot.ballot_ref : "the Commission draws it"} />
        <Kpi testId="ballot-kpi-sections" label="Sections on the paper" accent="violet"
             value={ballot ? ballot.sections.length : 0}
             sub={ballot ? `${ballot.sections.reduce((n, s) => n + s.options.length, 0)} choices`
               : "posts and questions"} />
        <Kpi testId="ballot-kpi-turnout" label="Turnout" accent="teal"
             value={`${turnout?.percent ?? 0}%`}
             sub={`${turnout?.cast ?? 0} of ${turnout?.roll ?? 0} on the roll`} />
        <Kpi testId="ballot-kpi-window" label="Voting window" accent="emerald" icon={Lock}
             value={words(data.window_state || "no window")}
             sub={ballot?.voting_window?.closes_at
               ? `closes ${pretty(ballot.voting_window.closes_at)}` : "not set"} />
      </div>

      <Panel title="The ballot paper" testId="panel-ballot"
             kicker={frozen
               ? "Frozen — nothing on this paper can change now"
               : "Drawn from the candidates the Commission approved"}
             actions={data.you_are_on_commission && (
               <div className="flex flex-wrap items-center gap-2">
                 {ballot && (
                   <Chip testId="ballot-status"
                         className={frozen
                           ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                           : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
                     {words(ballot.status)}
                   </Chip>
                 )}
                 {!frozen && (
                   <Button size="sm" className="h-8 rounded-full" data-testid="draw-ballot-button"
                           onClick={() => setBuild({ title: ballot?.title || "",
                             instructions: ballot?.instructions || "", allow_abstain: true,
                             questions: [] })}>
                     <Plus className="mr-1.5 h-3.5 w-3.5" />
                     {ballot ? "Redraw the paper" : "Draw the paper"}
                   </Button>
                 )}
                 {ballot && !frozen && (
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid="freeze-ballot" disabled={busy === "freeze"}
                           onClick={() => run("freeze",
                             () => api.post(`/elections/${electionRef}/ballot/freeze`),
                             "The paper is frozen — now set the voting window")}>
                     <Lock className="mr-1.5 h-3.5 w-3.5" /> Freeze it
                   </Button>
                 )}
                 {frozen && (
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid="set-voting-window"
                           onClick={() => setWindow({
                             opens_at: (ballot.voting_window?.opens_at || "").slice(0, 16),
                             closes_at: (ballot.voting_window?.closes_at || "").slice(0, 16),
                             existing: Boolean(ballot.voting_window?.opens_at) })}>
                     {ballot.voting_window?.opens_at ? "Change the voting window"
                       : "Set the voting window"}
                   </Button>
                 )}
                 {frozen && ballot.voting_window?.opens_at
                   && data.election_status === "ballot_ready" && (
                   <Button size="sm" className="h-8 rounded-full" data-testid="open-voting"
                           onClick={() => run("open",
                             () => api.post(`/elections/${electionRef}/step`,
                               { status: "voting_open", note: "Voting opened by the Commission" }),
                             "Voting is open — the roll has been told")}>
                     Open the vote
                   </Button>
                 )}
                 {data.election_status === "voting_open" && (
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid="close-voting"
                           onClick={() => run("close",
                             () => api.post(`/elections/${electionRef}/step`,
                               { status: "voting_closed", note: "Voting closed by the Commission" }),
                             "Voting is closed and the box is sealed")}>
                     Close the vote
                   </Button>
                 )}
               </div>
             )}>
        {!ballot ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="ballot-empty">
            No paper has been drawn yet. Once the candidates are settled the Commission draws the
            ballot from them.
          </p>
        ) : (
          <div className="space-y-3" data-testid="ballot-sections">
            <p className="text-[12px] leading-relaxed text-muted-foreground"
               data-testid="ballot-instructions">
              {ballot.instructions}
            </p>
            {ballot.sections.map((s, i) => (
              <div key={s.section_ref} className="rounded-xl border border-border p-3.5"
                   data-testid={`ballot-section-${s.section_ref}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[13px] font-semibold text-foreground">
                    {i + 1}. {s.title}
                  </p>
                  <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    {s.method_label}
                    {s.paper_kind === "multiple" ? ` · choose up to ${s.choose_up_to}` : ""}
                    {s.allow_abstain ? " · abstention offered" : ""}
                  </span>
                </div>
                {s.detail && (
                  <p className="mt-1 text-[11.5px] text-muted-foreground">{s.detail}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.options.map((o) => (
                    <span key={o.option_ref} data-testid={`ballot-option-${o.option_ref}`}
                          className="rounded-full border border-border px-3 py-1 text-[11.5px] text-foreground">
                      {o.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {turnout && (
        <Panel title="Turnout" testId="panel-turnout"
               kicker="Aggregate only while voting is open — no running totals, ever">
          <div className="flex flex-wrap items-center gap-3 text-[12.5px]">
            <span className="font-mono text-[13px] text-foreground" data-testid="turnout-figure">
              {turnout.cast} of {turnout.roll} ({turnout.percent}%)
            </span>
            <Chip testId="turnout-test"
                  className={turnout.turnout_met
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
              least turnout {turnout.minimum_turnout_percent}%
              {turnout.turnout_met ? " met" : " not met yet"}
            </Chip>
          </div>
          {turnout.marked_register && (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <p className="kicker">Marked register — who has voted</p>
                <div className="mt-1.5 space-y-1" data-testid="marked-register">
                  {turnout.marked_register.length === 0 && (
                    <p className="text-[11.5px] text-muted-foreground">Nobody yet.</p>
                  )}
                  {turnout.marked_register.map((v) => (
                    <p key={v.receipt} className="text-[11.5px] text-muted-foreground">
                      <CheckCircle2 className="mr-1 inline h-3 w-3 text-emerald-500" />
                      {v.name} · {pretty(v.at)}
                    </p>
                  ))}
                </div>
                <p className="mt-2 text-[10.5px] text-muted-foreground">
                  This register says who voted. It carries nothing about how they voted and cannot
                  be joined to any paper.
                </p>
              </div>
              <div>
                <p className="kicker">Still to vote</p>
                <p className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground"
                   data-testid="not-yet-voted">
                  {turnout.not_yet_voted.length
                    ? turnout.not_yet_voted.join(", ")
                    : "Everybody on the roll has voted."}
                </p>
              </div>
            </div>
          )}
        </Panel>
      )}

      {/* draw the paper */}
      <Dialog open={Boolean(build)} onOpenChange={(o) => !o && setBuild(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto"
                       data-testid="ballot-dialog">
          <DialogHeader><DialogTitle>Draw the ballot paper</DialogTitle>
            <DialogDescription>
              One section per contested post, in the method that post was declared under. Add any
              question you want the membership to answer at the same time.
            </DialogDescription></DialogHeader>
          <div className="space-y-3">
            <F label="Title on the paper">
              <Input data-testid="ballot-title" value={build?.title || ""}
                     onChange={(e) => setBuild({ ...build, title: e.target.value })} />
            </F>
            <F label="Instructions to the voter">
              <Textarea rows={3} data-testid="ballot-instructions-input"
                        value={build?.instructions || ""}
                        onChange={(e) => setBuild({ ...build, instructions: e.target.value })} />
            </F>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <span className="text-[12px] text-foreground">
                Offer an explicit abstention in every section
              </span>
              <Switch data-testid="ballot-abstain" checked={Boolean(build?.allow_abstain)}
                      onCheckedChange={(v) => setBuild({ ...build, allow_abstain: v })} />
            </div>
            <div className="flex items-center justify-between">
              <p className="kicker">Questions to put alongside the posts</p>
              <Button size="sm" variant="outline" className="h-8 rounded-full"
                      data-testid="add-question"
                      onClick={() => setBuild({ ...build,
                        questions: [...(build.questions || []),
                          { title: "", detail: "", options: ["For", "Against"],
                            allow_abstain: true }] })}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> A question
              </Button>
            </div>
            {(build?.questions || []).map((q, idx) => (
              <div key={`q-${idx}`} className="space-y-2 rounded-xl border border-border p-3"
                   data-testid={`question-form-${idx}`}>
                <F label="Question">
                  <Input data-testid={`question-title-${idx}`} value={q.title}
                         onChange={(e) => {
                           const questions = [...build.questions];
                           questions[idx] = { ...q, title: e.target.value };
                           setBuild({ ...build, questions });
                         }} />
                </F>
                <F label="Answers, separated by a comma">
                  <div className="flex items-center gap-2">
                    <Input data-testid={`question-options-${idx}`} value={q.options.join(", ")}
                           onChange={(e) => {
                             const questions = [...build.questions];
                             questions[idx] = { ...q,
                               options: e.target.value.split(",").map((o) => o.trim()) };
                             setBuild({ ...build, questions });
                           }} />
                    <Button size="icon" variant="ghost" className="h-9 w-9"
                            data-testid={`remove-question-${idx}`}
                            onClick={() => setBuild({ ...build,
                              questions: build.questions.filter((_, i) => i !== idx) })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </F>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="ballot-save" disabled={busy === "build"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("build",
                      () => api.post(`/elections/${electionRef}/ballot`, build),
                      "The draft paper is drawn — preview it, then freeze it")}>
              {busy === "build" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Draw it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* the voting window */}
      <Dialog open={Boolean(window_)} onOpenChange={(o) => !o && setWindow(null)}>
        <DialogContent className="max-w-lg" data-testid="voting-window-dialog">
          <DialogHeader><DialogTitle>
              {window_?.existing ? "Change the voting window" : "Set the voting window"}
            </DialogTitle>
            <DialogDescription>
              Voting opens and closes on the clock. Times are UTC.
            </DialogDescription></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <F label="Opens">
              <Input type="datetime-local" data-testid="voting-opens"
                     value={window_?.opens_at || ""}
                     onChange={(e) => setWindow({ ...window_, opens_at: e.target.value })} />
            </F>
            <F label="Closes">
              <Input type="datetime-local" data-testid="voting-closes"
                     value={window_?.closes_at || ""}
                     onChange={(e) => setWindow({ ...window_, closes_at: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button data-testid="voting-window-save" disabled={busy === "window"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("window",
                      () => api.put(`/elections/${electionRef}/voting-window`,
                        { opens_at: iso(window_.opens_at), closes_at: iso(window_.closes_at) }),
                      "The voting window is set and the roll has been told")}>
              {busy === "window" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Set it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
